import React, { useState } from 'react';

import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const SearchFilters = ({ filters, onFiltersChange, onClearFilters, resultCount, isMobile, isOpen, onToggle }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const institutionTypes = [
    { value: 'universite-publique', label: 'Université Publique' },
    { value: 'universite-privee', label: 'Université Privée' },
    { value: 'ecole-ingenieur', label: 'École d\'Ingénieur' },
    { value: 'ecole-commerce', label: 'École de Commerce' },
    { value: 'institut-specialise', label: 'Institut Spécialisé' },
    { value: 'ecole-art', label: 'École d\'Art et Design' },
    { value: 'formation-professionnelle', label: 'Formation Professionnelle' }
  ];

  const cities = [
    { value: 'casablanca', label: 'Casablanca' },
    { value: 'rabat', label: 'Rabat' },
    { value: 'marrakech', label: 'Marrakech' },
    { value: 'fes', label: 'Fès' },
    { value: 'tanger', label: 'Tanger' },
    { value: 'agadir', label: 'Agadir' },
    { value: 'meknes', label: 'Meknès' },
    { value: 'oujda', label: 'Oujda' },
    { value: 'tetouan', label: 'Tétouan' },
    { value: 'kenitra', label: 'Kénitra' }
  ];

  const bacTypes = [
    { value: 'sciences-maths', label: 'Sciences Mathématiques' },
    { value: 'sciences-physiques', label: 'Sciences Physiques' },
    { value: 'svt', label: 'Sciences de la Vie et de la Terre' },
    { value: 'sciences-economiques', label: 'Sciences Économiques' },
    { value: 'lettres-modernes', label: 'Lettres Modernes' },
    { value: 'lettres-originales', label: 'Lettres Originales' },
    { value: 'sciences-humaines', label: 'Sciences Humaines' },
    { value: 'arts-appliques', label: 'Arts Appliqués' }
  ];

  const domains = [
    { value: 'ingenierie', label: 'Ingénierie et Technologie' },
    { value: 'medecine', label: 'Médecine et Santé' },
    { value: 'economie-gestion', label: 'Économie et Gestion' },
    { value: 'droit', label: 'Droit et Sciences Politiques' },
    { value: 'lettres-langues', label: 'Lettres et Langues' },
    { value: 'sciences-humaines', label: 'Sciences Humaines' },
    { value: 'arts-design', label: 'Arts et Design' },
    { value: 'agriculture', label: 'Agriculture et Environnement' },
    { value: 'tourisme', label: 'Tourisme et Hôtellerie' },
    { value: 'communication', label: 'Communication et Médias' }
  ];

  const feeRanges = [
    { value: 'gratuit', label: 'Gratuit (Public)' },
    { value: '0-10000', label: 'Moins de 10 000 MAD' },
    { value: '10000-30000', label: '10 000 - 30 000 MAD' },
    { value: '30000-50000', label: '30 000 - 50 000 MAD' },
    { value: '50000-100000', label: '50 000 - 100 000 MAD' },
    { value: '100000+', label: 'Plus de 100 000 MAD' }
  ];

  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...localFilters, [key]: value };
    setLocalFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handleArrayFilterChange = (key, value, checked) => {
    const currentArray = localFilters?.[key] || [];
    const updatedArray = checked 
      ? [...currentArray, value]
      : currentArray?.filter(item => item !== value);
    
    handleFilterChange(key, updatedArray);
  };

  const handleClearAll = () => {
    const clearedFilters = {
      search: '',
      institutionType: [],
      city: [],
      bacType: [],
      domain: [],
      feeRange: [],
      hasScholarships: false,
      hasInternships: false,
      hasExchange: false
    };
    setLocalFilters(clearedFilters);
    onClearFilters();
  };

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Search Input */}
      <div>
        <Input
          type="search"
          placeholder="Rechercher par nom d'établissement..."
          value={localFilters?.search || ''}
          onChange={(e) => handleFilterChange('search', e?.target?.value)}
          className="w-full"
        />
      </div>

      {/* Institution Type */}
      <div>
        <h3 className="font-medium text-foreground mb-3">Type d'établissement</h3>
        <div className="space-y-2">
          {institutionTypes?.map((type) => (
            <Checkbox
              key={type?.value}
              label={type?.label}
              checked={(localFilters?.institutionType || [])?.includes(type?.value)}
              onChange={(e) => handleArrayFilterChange('institutionType', type?.value, e?.target?.checked)}
            />
          ))}
        </div>
      </div>

      {/* City */}
      <div>
        <Select
          label="Ville"
          placeholder="Sélectionner une ville"
          options={cities}
          value={localFilters?.city || []}
          onChange={(value) => handleFilterChange('city', value)}
          multiple
          searchable
          clearable
        />
      </div>

      {/* Bac Type */}
      <div>
        <h3 className="font-medium text-foreground mb-3">Type de Baccalauréat accepté</h3>
        <div className="space-y-2">
          {bacTypes?.map((bac) => (
            <Checkbox
              key={bac?.value}
              label={bac?.label}
              checked={(localFilters?.bacType || [])?.includes(bac?.value)}
              onChange={(e) => handleArrayFilterChange('bacType', bac?.value, e?.target?.checked)}
            />
          ))}
        </div>
      </div>

      {/* Domain */}
      <div>
        <Select
          label="Domaine d'études"
          placeholder="Sélectionner un domaine"
          options={domains}
          value={localFilters?.domain || []}
          onChange={(value) => handleFilterChange('domain', value)}
          multiple
          searchable
          clearable
        />
      </div>

      {/* Fee Range */}
      <div>
        <h3 className="font-medium text-foreground mb-3">Frais de scolarité</h3>
        <div className="space-y-2">
          {feeRanges?.map((range) => (
            <Checkbox
              key={range?.value}
              label={range?.label}
              checked={(localFilters?.feeRange || [])?.includes(range?.value)}
              onChange={(e) => handleArrayFilterChange('feeRange', range?.value, e?.target?.checked)}
            />
          ))}
        </div>
      </div>

      {/* Additional Features */}
      <div>
        <h3 className="font-medium text-foreground mb-3">Services disponibles</h3>
        <div className="space-y-2">
          <Checkbox
            label="Bourses d'études disponibles"
            checked={localFilters?.hasScholarships || false}
            onChange={(e) => handleFilterChange('hasScholarships', e?.target?.checked)}
          />
          <Checkbox
            label="Stages en entreprise"
            checked={localFilters?.hasInternships || false}
            onChange={(e) => handleFilterChange('hasInternships', e?.target?.checked)}
          />
          <Checkbox
            label="Programmes d'échange"
            checked={localFilters?.hasExchange || false}
            onChange={(e) => handleFilterChange('hasExchange', e?.target?.checked)}
          />
        </div>
      </div>

      {/* Clear Filters */}
      <Button
        variant="outline"
        onClick={handleClearAll}
        iconName="X"
        iconPosition="left"
        fullWidth
      >
        Effacer tous les filtres
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-4">
          <Button
            variant="outline"
            onClick={onToggle}
            iconName="Filter"
            iconPosition="left"
            fullWidth
          >
            Filtres ({resultCount} résultats)
          </Button>
        </div>

        {/* Mobile Filter Overlay */}
        {isOpen && (
          <div className="lg:hidden fixed inset-0 z-50 bg-background">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h2 className="text-lg font-semibold">Filtres de recherche</h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onToggle}
                  iconName="X"
                />
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <FilterContent />
              </div>
              <div className="p-4 border-t border-border">
                <Button onClick={onToggle} fullWidth>
                  Voir {resultCount} résultats
                </Button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="hidden lg:block w-80 bg-card border border-border rounded-lg p-6 h-fit sticky top-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Filtres</h2>
        <span className="text-sm text-muted-foreground">{resultCount} résultats</span>
      </div>
      <FilterContent />
    </div>
  );
};

export default SearchFilters;